import React from 'react';

import { Dialog, Autocomplete, Input, showToast } from '@shared/components';
import { EXTENSION_TYPE, VALIDATIONS, TOAST_TYPES } from '@shared/constants';

import { getSortedList } from '@utils';

import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import useStyles from './style';

const defaultState = {
  extensionName: '',
  startRange: '',
  endRange: '',
  selectedExtensionType: null,
  selectedSite: null,
  excludeNonCustomRules: false,
  downloadBlankTemplate: false,
  errors: {
    startRange: '',
    endRange: '',
  },
};

const FilterComponent = ({
  sites = [],
  isOpen = false,
  regexString = '',
  selectedExtensionType = null,
  selectedSite = null,
  excludeRulesChecked = false,
  excludeAllRules = false,
  onClose = () => {},
  onSave = () => {},
}) => {
  const classes = useStyles();
  const [state, setState] = React.useState({
    ...defaultState,
  });

  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: option => option.label || '',
  });

  const validate = ({ startRange, endRange }) => {
    let isValid = true;
    if (+endRange <= +startRange) {
      showToast(
        'End Range should be greater than Start Range.',
        TOAST_TYPES.ERROR
      );
      isValid = false;
    }
    return isValid;
  };

  //HANDLERS
  const handleSave = () => {
    if (state.endRange && !validate(state)) {
      return false;
    }
    onSave({
      extensionType: state.selectedExtensionType,
      site: state.selectedSite,
      startRange: state.startRange,
      endRange: state.endRange,
      extensionName: state.extensionName,
      excludeNonCustomRules: state.excludeNonCustomRules,
      downloadBlankTemplate: state.downloadBlankTemplate,
    });
    setState(defaultState);
  };
  const handleClose = () => {
    setState(defaultState);
    onClose();
  };

  const isSaveEnabled =
    state.extensionName ||
    state.selectedExtensionType ||
    state.selectedSite ||
    state.excludeNonCustomRules ||
    state.startRange ||
    state.downloadBlankTemplate;

  //JSX
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      classes={{
        paper: classes.filter,
      }}
    >
      <Dialog.Title hasClose />
      <Dialog.Content>
        <Typography variant='h5' className='bold-font'>
          Filter Options
        </Typography>
        <Typography variant='body1' className='color-text-secondary mt-1'>
          Define filter criteria below.
        </Typography>
        <Typography variant='body1' className='bold-font mt-4'>
          Extension Name
        </Typography>
        <Input
          type='text'
          placeholder='Extension Name (leave blank for all)'
          name='extensionName'
          fullWidth
          value={state.extensionName}
          disabled={state.downloadBlankTemplate}
          onChange={(event, error) => {
            let value = event.currentTarget.value;
            setState(prevState => ({
              ...prevState,
              extensionName: value,
            }));
          }}
        />
        <Typography variant='body1' className='bold-font mt-4'>
          Extension Range
        </Typography>
        <div className='d-flex mt-1 w-75'>
          <Input
            type='number'
            size='small'
            name='startRange'
            value={state.startRange}
            placeholder='Start'
            onKeyDown={event => {
              if (event.keyCode == 189 || event.keyCode == 187) {
                event.preventDefault();
                return false;
              }
            }}
            onChange={(event, error) => {
              let value = event.currentTarget.value;
              setState(prevState => ({
                ...prevState,
                startRange: value,
                errors: {
                  startRange: error,
                },
              }));
            }}
            validations={{
              [VALIDATIONS.MAX_LENGTH]: 8,
            }}
          />
          <Typography variant='body1' className='bold-font mt-4 mr-4 ml-4'>
            -
          </Typography>
          <Input
            type='number'
            name='endRange'
            value={state.endRange}
            placeholder='End'
            onKeyDown={event => {
              if (event.keyCode == 189 || event.keyCode == 187) {
                event.preventDefault();
                return false;
              }
            }}
            onChange={(event, error) => {
              let value = event.currentTarget.value;
              setState(prevState => ({
                ...prevState,
                endRange: value,
                errors: {
                  endRange: error,
                },
              }));
            }}
            validations={{
              [VALIDATIONS.MAX_LENGTH]: 8,
            }}
          />
        </div>
        <Typography variant='body1' className='bold-font mt-4'>
          Extension Type
        </Typography>
        <Autocomplete
          fullWidth
          disableClearable
          getOptionLabel={option => {
            return option.label || '';
          }}
          disabled={state.downloadBlankTemplate}
          value={state.selectedExtensionType}
          options={getSortedList(EXTENSION_TYPE)}
          filterOptions={filterOptions}
          onChange={(event, selectedOption) => {
            setState(prevState => ({
              ...prevState,
              selectedExtensionType: selectedOption,
            }));
          }}
          classes={{
            endAdornment: classes.endAdornment,
          }}
          placeholder='Select Extension Type'
        />
        <Typography variant='body1' className='bold-font mt-4'>
          Sites
        </Typography>
        <Autocomplete
          fullWidth
          disableClearable
          getOptionLabel={option => {
            return option.label || '';
          }}
          disabled={state.downloadBlankTemplate}
          value={state.selectedSite}
          options={getSortedList(sites)}
          filterOptions={filterOptions}
          onChange={(event, selectedOption) => {
            setState(prevState => ({
              ...prevState,
              selectedSite: selectedOption,
            }));
          }}
          classes={{
            endAdornment: classes.endAdornment,
          }}
          placeholder='Select Sites'
        />
        <div className='d-flex f-align-center'>
          <Checkbox
            color='primary'
            size='small'
            name='excludeNonCustomRules'
            checked={state.excludeNonCustomRules}
            disabled={state.downloadBlankTemplate}
            onChange={event => {
              setState(prevState => ({
                ...prevState,
                excludeNonCustomRules: event.target.checked,
              }));
            }}
          />
          <Typography variant='body2'>
            Exclude Business and After Hour Rules
          </Typography>
        </div>
        <div className='d-flex f-align-center'>
          <Checkbox
            color='primary'
            size='small'
            name='downloadBlankTemplate'
            checked={state.downloadBlankTemplate}
            onChange={event => {
              setState(prevState => ({
                ...defaultState,
                downloadBlankTemplate: event.target.checked,
              }));
            }}
          />
          <Typography variant='body2'>No Rules / Template Only</Typography>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          variant='outlined'
          className='bold-font mr-3 mb-4'
          onClick={handleClose}
        >
          <Typography variant='button' className='pl-4 pr-4'>
            Cancel
          </Typography>
        </Button>
        <Button
          variant='contained'
          color='primary'
          className='bold-font mr-4 mb-4 pl-1 pr-1'
          onClick={handleSave}
          disabled={
            !isSaveEnabled ||
            !!state.errors.startRange ||
            !!state.errors.endRange
          }
        >
          <Typography variant='button' className='pl-8 pr-8'>
            Save
          </Typography>
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
export default FilterComponent;
